import styled from 'styled-components';
import { format } from 'date-fns';
import { useState } from 'react';

import { getComma } from '@/util/format';
import { ForeignCurrencyItem } from '@/types/fx.type';
import ICON_BIN from '@/assets/refund/bin.png';
import { usePatchCancelForeignCurrency } from '@/hooks/usePatchCancelForeignCurrency';
import FXReceiptCancelModal from './FXReceiptCancelModal';
import FXReceiptCancelCompleteModal from './FXReceiptCancelCompleteModal';

interface FXReceiptItemProps {
  foreignCurrency: ForeignCurrencyItem;
  index: number;
  currentPage: number;
}

function FXReceiptItem({
  foreignCurrency,
  index,
  currentPage,
}: FXReceiptItemProps) {
  // 구조 분해 할당
  const { createdDate, foreignCurrencyId, amount, exchangeRate, isCanceled } =
    foreignCurrency;

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const { cancelForeignCurrencyAsync, isLoading } =
    usePatchCancelForeignCurrency({
      onSuccess: () => {
        setIsCancelModalOpen(false);
        setIsCompleteModalOpen(true);
      },
      onError: (error) => {
        console.error('Foreign currency cancel error:', error);
        alert('외화 수납 취소에 실패했습니다. 다시 시도해주세요.');
      },
    });

  // 취소 상태에 따른 활성화 여부
  const isActive = !isCanceled;

  // No. 계산 (페이지 번호 * 페이지 크기 + 인덱스 + 1)
  const itemNumber = currentPage * 10 + index + 1;

  // 취소 상태 텍스트
  const getStatusText = (canceled: boolean) => {
    return canceled ? '발행 취소' : '발행 완료';
  };

  const handleCancelClick = () => {
    setIsCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    try {
      await cancelForeignCurrencyAsync({
        foreignCurrencyId,
      });
    } catch (error) {
      // 에러는 onError 콜백에서 처리됨
    }
  };

  return (
    <>
      <TableRow>
        {/* No. */}
        <TableCell active={isActive}>{itemNumber}</TableCell>

        {/* 발행 일시 */}
        <TableCell active={isActive}>
          {createdDate ? format(new Date(createdDate), 'yyyy-MM-dd HH:mm') : ''}
        </TableCell>

        {/* 금액 */}
        <AmountCell active={isActive}>{getComma(amount)}원</AmountCell>

        {/* 환율 */}
        <TableCell active={isActive} style={{ color: '#A7A7A7' }}>
          {exchangeRate === 0 ? '-' : `${getComma(exchangeRate)}원`}
        </TableCell>

        {/* 발행 상태 */}
        <TableCell active={isActive}>{getStatusText(isCanceled)}</TableCell>

        {/* 취소 버튼 */}
        <TableCell>
          {isActive && (
            <CancelButton onClick={handleCancelClick} disabled={isLoading}>
              <img src={ICON_BIN} alt='취소' />
            </CancelButton>
          )}
        </TableCell>
      </TableRow>

      <FXReceiptCancelModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        foreignCurrencyData={{
          createdDate,
          amount,
        }}
      />

      <FXReceiptCancelCompleteModal
        isOpen={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
      />
    </>
  );
}

// 스타일 컴포넌트
const TableRow = styled.tr`
  border-bottom: 1px solid ${(props) => props.theme.mono[10]};
  background-color: ${(props) => props.theme.pointColors.white};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #e9f0fe;
  }
`;

const TableCell = styled.td<{
  active?: boolean;
}>`
  color: ${(props) =>
    props.active ? props.theme.mono[80] : props.theme.mono[35]};
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
  box-sizing: border-box;
  text-align: center;

  /* WebTitle/14_SB */
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;

  @media ${(props) => props.theme.mediumView} {
    padding: 12px 20px;
  }

  /* 버튼을 포함한 셀의 경우 flex로 중앙 정렬 */
  &:last-child {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const AmountCell = styled(TableCell)`
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default FXReceiptItem;
